import React, { useEffect, useState, useCallback } from 'react';
import { Mail, RefreshCw, CheckCircle2, AlertCircle, Loader2, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { salesAPI } from '@/lib/api';
import { MboxExportGuideButton } from './MboxExportGuideDialog';

export default function GmailSyncCard({ onSynced }) {
    const [connection, setConnection] = useState(null);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [since, setSince] = useState('2026-01-01');
    const [lastRun, setLastRun] = useState(null);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const r = await salesAPI.gmailStatus();
            setLastRun(r.data.last);
            setHistory(r.data.history || []);
            if (r.data.last?.status === 'running') {
                setSyncing(true);
            } else if (syncing && r.data.last?.status === 'done') {
                setSyncing(false);
                toast.success(`Sync terminée : ${r.data.last.messages_processed} nouveaux mails, ${r.data.last.entries_upserted} ventes`);
                if (onSynced) onSynced();
            } else if (syncing && r.data.last?.status === 'failed') {
                setSyncing(false);
                toast.error(`Échec sync : ${r.data.last.errors?.[0]?.error || 'erreur inconnue'}`);
            }
        } catch (e) { /* silent */ }
    }, [syncing, onSynced]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    // Poll while running
    useEffect(() => {
        if (!syncing) return;
        const id = setInterval(fetchStatus, 2500);
        return () => clearInterval(id);
    }, [syncing, fetchStatus]);

    const handleTest = async () => {
        setTesting(true);
        try {
            const r = await salesAPI.gmailTest();
            setConnection(r.data);
            if (r.data.ok) {
                toast.success(`Connecté à ${r.data.address} (label : ${r.data.label})`);
            } else {
                toast.error(`Connexion KO : ${r.data.error}`);
            }
        } catch (e) {
            toast.error('Erreur de test');
        } finally {
            setTesting(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        try {
            await salesAPI.gmailSync(since);
            toast.info('Synchronisation lancée en arrière-plan…');
            setTimeout(fetchStatus, 1500);
        } catch (e) {
            setSyncing(false);
            toast.error(e.response?.data?.detail || 'Erreur de lancement');
        }
    };

    const handleClearProcessed = async () => {
        if (!window.confirm('Réinitialiser la liste des mails déjà traités ?\n\nCela permettra au prochain sync de re-télécharger TOUS les mails (les ventes en base ne seront pas dupliquées grâce à l\'idempotence).')) return;
        try {
            const r = await salesAPI.gmailClearProcessed();
            toast.success(`${r.data.cleared} marqueurs effacés`);
            fetchStatus();
        } catch (e) {
            toast.error('Erreur');
        }
    };

    const fmtDate = (iso) => {
        if (!iso) return '—';
        try {
            const d = new Date(iso);
            return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
        } catch (_e) { return iso; }
    };

    return (
        <Card data-testid="gmail-sync-card">
            <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        Import automatique depuis Gmail
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {connection?.ok && (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />Connecté
                            </Badge>
                        )}
                        {connection && !connection.ok && (
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                                <AlertCircle className="w-3 h-3 mr-1" />Erreur
                            </Badge>
                        )}
                        <MboxExportGuideButton />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleTest}
                            disabled={testing}
                            className="h-8"
                            data-testid="gmail-test-btn"
                        >
                            {testing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                            Tester
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Connection summary */}
                <div className="text-xs text-slate-600 flex flex-wrap items-center gap-3">
                    <span>📬 <span className="font-mono">{connection?.address || '—'}</span></span>
                    <span>🏷️ Label : <Badge variant="outline" className="text-xs">{connection?.label || 'Ventes'}</Badge></span>
                    <span>🕐 Auto-sync : <span className="font-semibold">05h / 06h30 / 08h</span> ({connection?.timezone || 'Martinique'})</span>
                </div>

                {/* Sync controls */}
                <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
                    <div>
                        <Label className="text-xs text-slate-500">Récupérer depuis le</Label>
                        <Input
                            type="date"
                            value={since}
                            onChange={(e) => setSince(e.target.value)}
                            className="h-8 text-xs w-[140px]"
                            data-testid="gmail-since-input"
                        />
                    </div>
                    <Button
                        size="sm"
                        onClick={handleSync}
                        disabled={syncing || !connection?.ok}
                        className="bg-blue-500 hover:bg-blue-600 h-8"
                        data-testid="gmail-sync-btn"
                    >
                        {syncing ? (
                            <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Synchronisation…</>
                        ) : (
                            <><Mail className="w-3.5 h-3.5 mr-1" /> Synchroniser maintenant</>
                        )}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowHistory(v => !v)}
                        className="h-8 text-xs text-slate-500"
                        data-testid="gmail-history-toggle"
                    >
                        <History className="w-3.5 h-3.5 mr-1" />
                        {showHistory ? 'Masquer' : 'Historique'}
                    </Button>
                </div>

                {/* Last run summary */}
                {lastRun && (
                    <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs space-y-1" data-testid="gmail-last-run">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-slate-500">Dernière sync :</span>
                            <span className="font-semibold text-slate-700">{fmtDate(lastRun.started_at)}</span>
                            {lastRun.status === 'running' && <Badge className="bg-blue-100 text-blue-700">En cours…</Badge>}
                            {lastRun.status === 'done' && <Badge className="bg-emerald-100 text-emerald-700">OK</Badge>}
                            {lastRun.status === 'failed' && <Badge className="bg-red-100 text-red-700">Échec</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            <span><span className="text-slate-400">📥 Mails vus :</span> {lastRun.messages_seen || 0}</span>
                            <span><span className="text-slate-400">✅ Traités :</span> {lastRun.messages_processed || 0}</span>
                            <span><span className="text-slate-400">⏭️ Déjà importés :</span> {lastRun.messages_skipped || 0}</span>
                            <span><span className="text-slate-400">📊 Ventes ajoutées :</span> <span className="font-semibold text-emerald-600">{lastRun.entries_upserted || 0}</span></span>
                            {lastRun.errors?.length > 0 && (
                                <span className="text-red-600">❌ {lastRun.errors.length} erreur(s)</span>
                            )}
                        </div>
                        {lastRun.errors?.length > 0 && (
                            <details className="mt-1">
                                <summary className="cursor-pointer text-red-500 text-[11px]">Voir les erreurs</summary>
                                <ul className="mt-1 space-y-0.5 text-[10px] text-slate-600 max-h-[120px] overflow-y-auto">
                                    {lastRun.errors.map((e, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span className="text-slate-400">{e.date || ''}</span>
                                            <span className="text-slate-500">{e.subject || ''}</span>
                                            <span className="text-red-500">{e.error}</span>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                )}

                {/* History panel */}
                {showHistory && history.length > 0 && (
                    <div className="bg-white border border-slate-200 rounded-md">
                        <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 border-b">
                            <span className="text-xs font-semibold text-slate-600">Historique récent ({history.length})</span>
                            <button
                                type="button"
                                onClick={handleClearProcessed}
                                className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-1"
                                title="Réinitialiser les marqueurs (utile pour re-traiter manuellement)"
                                data-testid="gmail-clear-processed"
                            >
                                <Trash2 className="w-3 h-3" /> Réinitialiser marqueurs
                            </button>
                        </div>
                        <div className="max-h-[240px] overflow-y-auto">
                            <table className="w-full text-[11px]">
                                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                                    <tr>
                                        <th className="text-left px-2 py-1">Date</th>
                                        <th className="text-left px-2 py-1">Depuis</th>
                                        <th className="text-center px-2 py-1">Statut</th>
                                        <th className="text-right px-2 py-1">Vus</th>
                                        <th className="text-right px-2 py-1">Traités</th>
                                        <th className="text-right px-2 py-1">Ventes</th>
                                        <th className="text-right px-2 py-1">Err.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((r, i) => (
                                        <tr key={i} className="border-t border-slate-100">
                                            <td className="px-2 py-1 whitespace-nowrap">{fmtDate(r.started_at)}</td>
                                            <td className="px-2 py-1">{r.since}</td>
                                            <td className="px-2 py-1 text-center">
                                                {r.status === 'running' && <Badge className="bg-blue-100 text-blue-700 text-[9px]">…</Badge>}
                                                {r.status === 'done' && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">OK</Badge>}
                                                {r.status === 'failed' && <Badge className="bg-red-100 text-red-700 text-[9px]">KO</Badge>}
                                                {!r.status && <Badge className="bg-slate-100 text-slate-500 text-[9px]">?</Badge>}
                                                {r.source === 'scheduler' && <span className="ml-1 text-[9px] text-slate-400">auto</span>}
                                            </td>
                                            <td className="px-2 py-1 text-right tabular-nums">{r.messages_seen || 0}</td>
                                            <td className="px-2 py-1 text-right tabular-nums">{r.messages_processed || 0}</td>
                                            <td className="px-2 py-1 text-right tabular-nums font-semibold text-emerald-600">{r.entries_upserted || 0}</td>
                                            <td className="px-2 py-1 text-right tabular-nums">{r.errors?.length || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
